/**
 * Graph view data shapes.
 *
 * The graph is a READ-ONLY derived projection (invariant 15): it is built from
 * the Dexie search mirror joined to the personal workspace snapshot, and it
 * never opens a note document, never writes a Y.Doc, and never creates items.
 */
import type { WorkspaceItem } from "@hydra-notes/shared";

export type GraphNodeKind = "note" | "tag" | "attachment" | "unresolved";
export type GraphLinkKind = "ref" | "tag" | "attachment" | "unresolved";

/**
 * Node ids are namespaced so a tag "recipes" and a note titled "recipes" can
 * never collapse into one node.
 */
export const nodeKey = {
  note: (itemId: string) => `n:${itemId}`,
  /** tag is already lowercased with no leading # */
  tag: (tag: string) => `t:${tag}`,
  attachment: (key: string) => `a:${key}`,
  /** target is already normalized (trimmed, lowercased) */
  unresolved: (target: string) => `u:${target}`,
} as const;

export interface GraphNode {
  id: string;
  /** Position in GraphData.nodes. Assigned by indexGraph. */
  index: number;
  kind: GraphNodeKind;
  /** Note title, "#tag", attachment filename, or the raw unresolved target. */
  label: string;
  /** Notes only: the WorkspaceItem id, used by openItem. */
  itemId?: string;
  /** Notes only: "Projects/2026/Roadmap". */
  path?: string;
  pathLower: string;
  titleLower: string;
  /** Notes: item tags + body hashtags. Tag nodes: their own tag. */
  tags: string[];
  /** Attachment nodes: resolvable href for /api/attachments refs. */
  href?: string;
  /**
   * Unresolved nodes whose title belongs to a card, folder, task list or
   * archived note. Clicking navigates rather than offering to create.
   */
  resolvesOffGraph?: boolean;
  /** Notes only. Drives the creation-order animation. */
  createdAt?: number;
  updatedAt?: number;
  /** Degree in the FILTERED graph; recomputed by applyFilters. */
  degree: number;
  /** Free-form hex from the first matching group, or undefined for default. */
  groupColor?: string;
  /**
   * Deterministic hue from the note's top-level folder. Used only when no
   * group matched AND the "Color by folder" display toggle is on; root-level
   * notes have none and keep the neutral default.
   */
  autoColor?: string;
  /** Local graph only: hops from the root. */
  depth?: number;
  /** Playback ordering: when this node first exists. */
  entranceAt: number;
}

export interface GraphLink {
  source: string;
  target: string;
  kind: GraphLinkKind;
  /** max(entranceAt of both endpoints). */
  entranceAt: number;
  /** max(index of both endpoints). Links are sorted by this, so the creation
   *  animation's live links are exactly a prefix of the array. */
  maxIndex: number;
}

export interface GraphAdjacency {
  out: Set<string>;
  in: Set<string>;
}

export interface GraphStats {
  notes: number;
  tags: number;
  attachments: number;
  unresolved: number;
  links: number;
  orphans: number;
  /** Notes sharing a title with an earlier note; a diagnostic, not an error. */
  titleCollisions: number;
}

export interface GraphData {
  /** Sorted by entranceAt: playback is then a growing prefix. */
  nodes: GraphNode[];
  /** Sorted by entranceAt, same reason. */
  links: GraphLink[];
  byId: Map<string, GraphNode>;
  adjacency: Map<string, GraphAdjacency>;
  stats: GraphStats;
}

/** Lowercased note bodies. Retained only while a live query has bare terms. */
export type GraphCorpus = Map<string, string>;

export const EMPTY_GRAPH: GraphData = {
  nodes: [],
  links: [],
  byId: new Map(),
  adjacency: new Map(),
  stats: { notes: 0, tags: 0, attachments: 0, unresolved: 0, links: 0, orphans: 0, titleCollisions: 0 },
};

export interface GraphInput {
  rows: import("@/data/search/client").GraphIndexRow[];
  items: Map<string, WorkspaceItem>;
  /** normalized title or alias -> note itemId. First wins, like openByTitle. */
  noteByTitle: Map<string, string>;
  /** Titles owned by cards, folders, task lists, or archived notes. */
  offGraphTitles: Set<string>;
  /** itemId -> "Folder/Sub/Title". */
  paths: Map<string, string>;
  collisions: number;
}
